import { nanoid } from 'nanoid';
import type { MarkdownDocument } from '@markbird/shared';
import { db, type DocSnapshot } from './db';

/** Minimum gap between automatic snapshots of the same document. */
export const SNAPSHOT_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
/** Cap on retained snapshots per document (older ones are pruned). */
export const SNAPSHOT_KEEP = 50;

/** All snapshots for a document, newest first. */
export function listSnapshots(docId: string): Promise<DocSnapshot[]> {
  return db.snapshots
    .where('[docId+createdAt]')
    .between([docId, 0], [docId, Infinity])
    .reverse()
    .toArray();
}

async function prune(docId: string): Promise<void> {
  const ids = await db.snapshots
    .where('[docId+createdAt]')
    .between([docId, 0], [docId, Infinity])
    .reverse()
    .offset(SNAPSHOT_KEEP)
    .primaryKeys();
  if (ids.length) await db.snapshots.bulkDelete(ids as string[]);
}

function latestSnapshot(docId: string): Promise<DocSnapshot | undefined> {
  return db.snapshots
    .where('[docId+createdAt]')
    .between([docId, 0], [docId, Infinity])
    .reverse()
    .first();
}

/**
 * Store a snapshot unless it would duplicate the latest one, then prune to the
 * retention cap. Returns whether a snapshot was written.
 *
 * The check and the write share one read-write transaction. They used to be
 * separate steps in separate callers, so "Save current version" and the
 * autosave's periodic snapshot could both read "no snapshot yet" before either
 * had written, and both wrote — the history then showed the same version twice.
 * IndexedDB runs read-write transactions on the same store one at a time, so
 * the second writer now sees the first one's snapshot.
 */
export async function captureSnapshot(
  doc: Pick<MarkdownDocument, 'id' | 'title' | 'content'>,
  reason: DocSnapshot['reason'] = 'auto',
  at: number = Date.now(),
): Promise<boolean> {
  if (!doc.id) return false;
  const docId = doc.id;
  return db.transaction('rw', db.snapshots, async () => {
    const latest = await latestSnapshot(docId);
    // An identical copy adds nothing restorable, whatever the reason: the
    // content can already be restored from the snapshot that exists.
    if (latest && latest.content === doc.content) return false;
    await db.snapshots.add({
      id: nanoid(),
      docId,
      title: doc.title,
      content: doc.content,
      createdAt: at,
      reason,
    });
    await prune(docId);
    return true;
  });
}

/**
 * Pure decision: should an automatic snapshot be taken now? Yes when the
 * content is non-empty and either there is no prior snapshot, or the content
 * changed and the interval has elapsed since the last one.
 */
export function shouldSnapshot(
  latest: { content: string; createdAt: number } | undefined,
  content: string,
  now: number,
): boolean {
  if (!content.trim()) return false;
  if (!latest) return true;
  if (latest.content === content) return false;
  return now - latest.createdAt >= SNAPSHOT_INTERVAL_MS;
}

/**
 * Take an automatic snapshot only if {@link shouldSnapshot} allows it — so
 * history holds periodic version points, not a copy per keystroke.
 */
export async function maybeSnapshot(
  doc: Pick<MarkdownDocument, 'id' | 'title' | 'content'>,
  now: number = Date.now(),
): Promise<boolean> {
  if (!doc.id) return false;
  const docId = doc.id;
  // Decide and write in one transaction, for the same reason as captureSnapshot:
  // the throttle is only a throttle if two callers cannot both pass it at once.
  return db.transaction('rw', db.snapshots, async () => {
    if (!shouldSnapshot(await latestSnapshot(docId), doc.content, now)) return false;
    return captureSnapshot(doc, 'auto', now);
  });
}

export function getSnapshot(id: string): Promise<DocSnapshot | undefined> {
  return db.snapshots.get(id);
}

/** Delete all snapshots for a document (e.g. when it is purged). */
export async function deleteSnapshots(docId: string): Promise<void> {
  const ids = await db.snapshots.where('docId').equals(docId).primaryKeys();
  if (ids.length) await db.snapshots.bulkDelete(ids as string[]);
}
